import { useState } from "react";
import {
  Bookmark,
  ChevronDown,
  Filter,
  MousePointerSquareDashed,
  Pencil,
  Plus,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { DEPARTMENTS, SHIPPING_METHODS, STATUS_VALUES } from "@/constants/workflow";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const TEXT_FILTERS = [
  ["elevation", "Elevation"],
  ["level", "Level"],
  ["type_code", "Type Code"],
  ["panel_type", "Panel Type"],
];

export function FilterBar({
  filters,
  setFilters,
  options,
  onReset,
  onSelectFiltered,
  matchCount,
  presets,
  onSavePreset,
  onDeletePreset,
  onApplyPreset,
  onEditPreset,
  selectionCount = 0,
  canManagePresets = false,
  dark = false,
  visibleFilters = null,
}) {
  const show = (key) => !visibleFilters || visibleFilters.includes(key);
  // Department status filters are collapsed by default and expand on demand.
  const [statusOpen, setStatusOpen] = useState(false);
  const activeStatusCount = DEPARTMENTS.filter((d) => filters[`status_${d.key}`]).length;
  const ui = dark
    ? {
        bar: "border-white/10 bg-[#0D1526]",
        field: "border-white/15 bg-white/5 text-slate-200 placeholder:text-slate-500",
        trigger: "border-white/15 bg-white/5 text-slate-300 hover:bg-white/10",
        menu: "border-white/10 bg-[#111A2B] text-slate-200",
        ghost: "border-white/15 text-slate-400 hover:bg-white/10",
      }
    : {
        bar: "border-slate-200 bg-white",
        field: "border-slate-200 bg-[#EEF2F8] text-slate-800 placeholder:text-slate-400",
        trigger: "border-slate-200 bg-[#EEF2F8] text-slate-700 hover:bg-slate-100",
        menu: "border-slate-200 bg-white text-slate-800",
        ghost: "border-slate-200 text-slate-600 hover:bg-slate-100",
      };
  const set = (key) => (value) =>
    setFilters({ ...filters, [key]: value === "__all__" ? "" : value });

  return (
    <div
      data-testid="filter-bar"
      className={`flex shrink-0 flex-wrap items-center gap-2 border-b px-3 py-2 ${ui.bar}`}
    >
      <Filter size={13} className={`shrink-0 ${dark ? "text-cyan-400" : "text-cyan-600"}`} />
      {show("panel_name") && (
      <input
        data-testid="filter-panel-name"
        value={filters.panel_name || ""}
        onChange={(e) => setFilters({ ...filters, panel_name: e.target.value })}
        placeholder="Search panel name…"
        className={`mono h-7 w-48 shrink-0 rounded-full border px-3 text-[11px] ${ui.field}`}
      />
      )}

      {TEXT_FILTERS.filter(([key]) => show(key)).map(([key, label]) => {
        const selected = (filters[key] || "").split(",").filter(Boolean);
        const toggle = (value) => {
          const next = selected.includes(value)
            ? selected.filter((v) => v !== value)
            : [...selected, value];
          setFilters({ ...filters, [key]: next.join(",") });
        };
        return (
          <DropdownMenu key={key}>
            <DropdownMenuTrigger asChild>
              <button
                data-testid={`filter-${key}`}
                className={`mono flex h-7 w-[124px] shrink-0 items-center justify-between gap-1 rounded-full border px-3 text-[10.5px] ${ui.trigger}`}
              >
                <span className="truncate">
                  {selected.length === 0
                    ? `All ${label}`
                    : selected.length === 1
                      ? selected[0]
                      : `${label} (${selected.length})`}
                </span>
                <ChevronDown size={12} className="shrink-0 opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="start"
              className={`max-h-72 overflow-y-auto border ${ui.menu}`}
            >
              <DropdownMenuItem
                data-testid={`filter-${key}-all`}
                onSelect={(event) => {
                  event.preventDefault();
                  setFilters({ ...filters, [key]: "" });
                }}
                className="text-xs"
              >
                All {label}
              </DropdownMenuItem>
              {(options[key] || []).map((value) => (
                <DropdownMenuCheckboxItem
                  key={value}
                  data-testid={`filter-${key}-option-${value}`}
                  checked={selected.includes(value)}
                  onCheckedChange={() => toggle(value)}
                  onSelect={(event) => event.preventDefault()}
                  className="mono text-xs"
                >
                  {value}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      })}

      {show("status") && (
        <button
          data-testid="filter-status-toggle"
          onClick={() => setStatusOpen((v) => !v)}
          className={`mono flex h-7 shrink-0 items-center gap-1.5 rounded-full border px-3 text-[10.5px] ${ui.trigger}`}
        >
          Statuses
          {activeStatusCount > 0 ? (
            <span
              data-testid="filter-status-active-count"
              className="grid h-4 min-w-4 place-items-center rounded-full bg-cyan-600 px-1 text-[9px] font-semibold text-white"
            >
              {activeStatusCount}
            </span>
          ) : null}
          <ChevronDown
            size={12}
            className={`shrink-0 opacity-60 transition-transform ${statusOpen ? "rotate-180" : ""}`}
          />
        </button>
      )}

      {show("status") && statusOpen && DEPARTMENTS.map((dept) => (
        <Select
          key={dept.key}
          value={filters[`status_${dept.key}`] || "__all__"}
          onValueChange={set(`status_${dept.key}`)}
        >
          <SelectTrigger
            data-testid={`filter-status-${dept.key}`}
            className={`h-7 w-[132px] shrink-0 rounded-full ${ui.trigger}`}
          >
            <SelectValue placeholder={dept.label} />
          </SelectTrigger>
          <SelectContent className={`border ${ui.menu}`}>
            <SelectItem value="__all__" className="text-xs">
              {dept.label}: All
            </SelectItem>
            {STATUS_VALUES[dept.key].map((value) => (
              <SelectItem key={value} value={value} className="text-xs">
                {value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ))}

      {show("pallet") && (
      <Select
        value={filters.pallet_code || "__all__"}
        onValueChange={set("pallet_code")}
      >
        <SelectTrigger
          data-testid="filter-pallet"
          className={`h-7 w-[118px] shrink-0 rounded-full ${ui.trigger}`}
        >
          <SelectValue placeholder="Pallet" />
        </SelectTrigger>
        <SelectContent className={`border ${ui.menu}`}>
          <SelectItem value="__all__" className="text-xs">
            All pallets
          </SelectItem>
          {(options.pallet_code || []).map((code) => (
            <SelectItem key={code} value={code} className="mono text-xs">
              {code}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      )}

      {show("shipping") && (
      <Select
        value={filters.shipping_method || "__all__"}
        onValueChange={set("shipping_method")}
      >
        <SelectTrigger
          data-testid="filter-shipping-method"
          className={`h-7 w-[136px] shrink-0 rounded-full ${ui.trigger}`}
        >
          <SelectValue placeholder="Shipping" />
        </SelectTrigger>
        <SelectContent className={`border ${ui.menu}`}>
          <SelectItem value="__all__" className="text-xs">
            All shipping
          </SelectItem>
          <SelectItem value="__null__" className="text-xs">
            No method
          </SelectItem>
          {SHIPPING_METHODS.map((method) => (
            <SelectItem key={method} value={method} className="text-xs">
              {method}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      )}

      {show("shipment_code") && (
      <input
        data-testid="filter-shipment-code"
        value={filters.shipment_code || ""}
        onChange={(e) => setFilters({ ...filters, shipment_code: e.target.value })}
        placeholder="Shipment code…"
        className={`mono h-7 w-[130px] shrink-0 rounded-full border px-3 text-[10.5px] ${ui.field}`}
      />
      )}

      {show("blocked") && (
      <Select
        value={filters.workflow_blocked || "__all__"}
        onValueChange={set("workflow_blocked")}
      >
        <SelectTrigger
          data-testid="filter-blocked"
          className={`h-7 w-[126px] shrink-0 rounded-full ${ui.trigger}`}
        >
          <SelectValue placeholder="Blocked" />
        </SelectTrigger>
        <SelectContent className={`border ${ui.menu}`}>
          <SelectItem value="__all__" className="text-xs">
            Blocked: All
          </SelectItem>
          <SelectItem value="true" className="text-xs">
            Blocked only
          </SelectItem>
          <SelectItem value="false" className="text-xs">
            Not blocked
          </SelectItem>
        </SelectContent>
      </Select>
      )}

      <div className="ml-auto flex items-center gap-2">
      <button
        data-testid="filter-select-panels"
        onClick={onSelectFiltered}
        className="mono flex shrink-0 items-center gap-1.5 rounded-full bg-cyan-600 px-3 py-1.5 text-[10px] uppercase tracking-wider text-white hover:bg-cyan-500"
      >
        <MousePointerSquareDashed size={11} /> Select ({matchCount})
      </button>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="filter-presets-trigger"
            className={`mono flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-wider ${ui.ghost}`}
          >
            <Bookmark size={11} /> Presets
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="end"
          className={`max-h-72 w-56 overflow-y-auto border ${ui.menu}`}
        >
          {canManagePresets ? (
            <DropdownMenuItem
              data-testid="save-preset-button"
              onSelect={onSavePreset}
              className="text-xs text-cyan-700"
            >
              <Plus size={12} className="mr-1.5" />{" "}
              {selectionCount
                ? `Save selected panels (${selectionCount})`
                : "Save current filters"}
            </DropdownMenuItem>
          ) : null}
          {presets.length === 0 ? (
            <p className="px-2 py-2 text-[11px] text-slate-500">No saved presets yet.</p>
          ) : (
            presets.map((preset) => (
              <div
                key={preset.name}
                className="flex items-center gap-1 px-1 py-0.5"
                data-testid={`preset-row-${preset.name}`}
              >
                <button
                  data-testid={`apply-preset-${preset.name}`}
                  onClick={() =>
                    onApplyPreset ? onApplyPreset(preset) : setFilters(preset.filters)
                  }
                  className="flex-1 truncate rounded px-2 py-1.5 text-left text-xs hover:bg-slate-100"
                >
                  {preset.name}
                  {preset.panel_uids?.length ? (
                    <span className="mono ml-1 text-[9px] text-slate-400">
                      {preset.panel_uids.length}p
                    </span>
                  ) : null}
                </button>
                {canManagePresets ? (
                  <button
                    data-testid={`edit-preset-${preset.name}`}
                    title="Rename or edit this preset"
                    onClick={() => onEditPreset?.(preset)}
                    className="grid h-6 w-6 place-items-center rounded text-slate-500 hover:bg-slate-100 hover:text-cyan-700"
                  >
                    <Pencil size={12} />
                  </button>
                ) : null}
                <button
                  data-testid={`delete-preset-${preset.name}`}
                  onClick={() => onDeletePreset(preset.name)}
                  className={`grid h-6 w-6 place-items-center rounded text-slate-500 hover:bg-red-50 hover:text-red-600 ${
                    canManagePresets ? "" : "hidden"
                  }`}
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <button
        data-testid="filter-reset"
        onClick={onReset}
        className={`okn-tool flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5 text-[10px] uppercase tracking-wider ${ui.ghost}`}
      >
        <RotateCcw size={11} /> Reset
      </button>
      </div>
    </div>
  );
}
